/*
  NOTE: I used @floating-ui/dom here, but it's the only file I use it in. I should either use this in other places (eg. Dropdown), or get rid of it.

  (That said, it's only ~8kb gzip, and only loaded on /gradient-generator, so it's not urgent.)
*/
import React from 'react';
import { styled } from '@linaria/react';
import {
  computePosition,
  flip as flipMiddleware,
  offset as offsetMiddleware,
  ComputePositionReturn,
} from '@floating-ui/dom';
import { motion, AnimatePresence } from 'framer-motion';

import Paper from '@/components/Paper';

import Tip from './FloatingBoxTip';

interface Props {
  wrapperRef: React.RefObject<HTMLDivElement>;
  isVisible: boolean;
  offset?: number;
  backgroundColor?: string;
  prefersReducedMotion: boolean;
  children: React.ReactNode;
}

function FloatingBox({
  wrapperRef,
  isVisible,
  offset = 16,
  backgroundColor = 'hsl(210deg 25% 92%)',
  prefersReducedMotion,
  children,
}: Props) {
  const tooltipRef = React.useRef(null);

  const [tooltipPosition, setTooltipPosition] =
    // We aren't providing all of the necessary fields, but it's fine since this gets overwritten in an effect.
    // @ts-ignore
    React.useState<ComputePositionReturn>({
      x: 0,
      y: 0,
    });

  React.useEffect(() => {
    async function calculateXY() {
      if (!isVisible || !wrapperRef.current || !tooltipRef.current) {
        return;
      }
      const tooltipPosition = await computePosition(
        wrapperRef.current,
        tooltipRef.current,
        {
          placement: 'top',
          middleware: [flipMiddleware(), offsetMiddleware(offset)],
        }
      );

      setTooltipPosition(tooltipPosition);
    }

    calculateXY();
  }, [offset, isVisible, wrapperRef]);

  const placement =
    tooltipPosition.placement === 'top' ? 'above' : 'below';

  let yOffset = 0;
  if (!prefersReducedMotion) {
    yOffset = placement === 'above' ? -25 : 25;
  }

  // NOTE: This doesn't work in dev mode thanks to Strict Mode.
  return (
    <AnimatePresence>
      {isVisible && (
        <Wrapper
          key="floating-box"
          initial={{
            opacity: 0,
            y: yOffset,
          }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: yOffset }}
          ref={tooltipRef}
          transition={{
            type: 'spring',
            damping: 25,
            stiffness: 500,
          }}
          style={{ top: tooltipPosition.y, left: tooltipPosition.x }}
        >
          <TooltipBox
            style={{
              '--background-color': backgroundColor,
            }}
          >
            {placement === 'above' ? <BottomTip /> : <TopTip />}

            {children}
          </TooltipBox>
        </Wrapper>
      )}
    </AnimatePresence>
  );
}

const Wrapper = styled(motion.div)`
  position: absolute;
  left: 0;
  right: 0;
  margin: auto;
  width: fit-content;
  /* prettier-ignore */
  filter:
    drop-shadow(0px 0px 1px hsl(var(--shadow-color) / 0.2))
    drop-shadow(0px 0px 3px hsl(var(--shadow-color) / 0.2))
    drop-shadow(0px 0px 9px hsl(var(--shadow-color) / 0.2));
  will-change: transform;
  backface-visibility: hidden;
`;

const TooltipBox = styled(Paper)`
  background: var(--background-color);
  padding: 16px;
  border-radius: 8px;
`;

const PositionedTip = styled(Tip)`
  position: absolute;
  left: 0;
  right: 0;
  margin: auto;
  color: white;
`;

const BottomTip = styled(PositionedTip)`
  bottom: 0;
  transform: translateY(calc(100% - 0.5px));
`;

const TopTip = styled(PositionedTip)`
  top: 0;
  transform: translateY(calc(-100% + 0.5px)) rotate(180deg);
`;

export default FloatingBox;
